import React, { useEffect } from "react";
import { inr, formatDateTime } from "@/lib/utils_pharma";

/**
 * Renders the 72mm thermal invoice.
 * - When `preview` is true, it is visible on-screen (used inside preview modal)
 * - The preview instance is also the printable instance so browser output
 *   matches the on-screen invoice exactly.
 */
export default function InvoicePrint({ bill, shop, preview = false }) {
  const paperWidth = ["58mm", "72mm", "80mm"].includes(shop?.thermal_paper_width) ? shop.thermal_paper_width : "72mm";

  useEffect(() => {
    document.documentElement.style.setProperty("--receipt-paper-width", paperWidth);
  }, [paperWidth]);

  if (!bill) return null;
  const dt = new Date(bill.created_at);
  const divider = "1px dashed #000";
  const paymentMode = String(bill.payment_mode || "-").toUpperCase();

  const classes = preview ? "invoice-preview" : "print-area print-only invoice-preview";
  const style = {
    width: paperWidth,
    minHeight: "auto",
    height: "auto",
    boxSizing: "border-box",
    padding: "2mm",
    fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
    fontSize: 10,
    lineHeight: 1.32,
    background: "#fff",
    color: "#000",
    border: preview ? "1px dashed #ccc" : "none",
    margin: preview ? "0 auto" : 0,
    overflow: "visible",
    pageBreakInside: "avoid",
    breakInside: "avoid",
  };

  return (
    <div className={classes} style={style} data-testid="invoice-print">
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        {shop?.logo_data_url && (
          <img
            src={shop.logo_data_url}
            alt="logo"
            style={{ maxWidth: "40mm", maxHeight: "20mm", display: "block", margin: "0 auto 2px", filter: "grayscale(1)" }}
          />
        )}
        <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: 0.2, textTransform: "uppercase" }}>{shop?.name || "Pharmacy"}</div>
        {shop?.address && <div style={{ fontSize: 9, lineHeight: 1.25 }}>{shop.address}</div>}
        {shop?.phone && <div style={{ fontSize: 9, lineHeight: 1.25 }}>Ph: {shop.phone}</div>}
        {shop?.email && <div style={{ fontSize: 8.5, lineHeight: 1.25 }}>{shop.email}</div>}
        {shop?.show_gstin_on_bill !== false && shop?.gstin && <div style={{ fontSize: 8.5, lineHeight: 1.25 }}>GSTIN: {shop.gstin}</div>}
        {shop?.license_no && <div style={{ fontSize: 8.5, lineHeight: 1.25 }}>DL: {shop.license_no}</div>}
      </div>

      <div style={{ borderTop: divider, borderBottom: divider, padding: "3px 0", fontSize: 9, lineHeight: 1.35 }}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>Bill: {bill.bill_no}</span>
          <span>{formatDateTime(bill.created_at || dt)}</span>
        </div>
        <div>Customer: {bill.customer_name}</div>
        {bill.customer_phone && <div>Ph: {bill.customer_phone}</div>}
      </div>

      <div style={{ borderBottom: divider, padding: "4px 0 3px", fontSize: 8.5, fontWeight: 700, textTransform: "uppercase" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 15mm 21mm", columnGap: 3 }}>
          <span>Item</span>
          <span style={{ textAlign: "center" }}>Qty</span>
          <span style={{ textAlign: "right" }}>Amount</span>
        </div>
      </div>

      <div style={{ fontSize: 9, lineHeight: 1.32 }}>
        {bill.items.map((it, i) => {
          const gross = it.mrp * it.quantity;
          const discountAmount = gross * ((it.discount_percent || 0) / 100);
          const line = it.mrp * it.quantity * (1 - (it.discount_percent || 0) / 100);
          return (
            <div key={i} style={{ padding: "4px 0", borderBottom: i === bill.items.length - 1 ? "none" : "1px dotted #777" }}>
              <div style={{ fontWeight: 700, fontSize: 9.5 }}>{it.medicine_name}</div>
              <div style={{ fontSize: 8, color: "#111" }}>
                B: {it.batch_no || "-"} | HSN: {it.hsn_code || "-"} | GST: {it.gst_percent}%
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 15mm 21mm", columnGap: 3, alignItems: "baseline", marginTop: 1 }}>
                <span></span>
                <span style={{ textAlign: "center", whiteSpace: "nowrap" }}>{it.quantity} {it.selling_unit || ""} × {inr(it.mrp)}</span>
                <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>{inr(line)}</span>
              </div>
              {it.discount_percent ? (
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 8.5, marginTop: 1 }}>
                  <span>Discount: -{inr(discountAmount)}</span>
                  <span>{it.discount_percent}%</span>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>

      <div style={{ borderTop: divider, borderBottom: divider, marginTop: 4, padding: "4px 0", fontSize: 9, lineHeight: 1.35 }}>
        <Row l="Subtotal" v={inr(bill.subtotal)} />
        <Row l="Discount" v={`- ${inr(bill.total_discount)}`} />
        {shop?.tax_applicable !== false && (
          <>
            <Row l="Taxable" v={inr(bill.taxable_amount)} />
            <Row l="CGST" v={inr(bill.cgst)} />
            <Row l="SGST" v={inr(bill.sgst)} />
          </>
        )}
        <div style={{ borderTop: divider, marginTop: 3, paddingTop: 3, fontWeight: 800, fontSize: 12 }}>
          <Row l="TOTAL" v={inr(bill.grand_total)} />
        </div>
      </div>

      <div style={{ borderBottom: divider, padding: "4px 0", fontSize: 9, lineHeight: 1.35 }}>
        {bill.payment_mode === "Split" && Array.isArray(bill.payments) && bill.payments.length > 0 ? (
          <>
            <div style={{ fontWeight: 700 }}>Payment</div>
            {bill.payments.map((p, idx) => (
              <Row key={idx} l={p.mode} v={inr(p.amount)} />
            ))}
          </>
        ) : (
          <>
            <div style={{ fontWeight: 700 }}>Payment Mode</div>
            <div style={{ fontWeight: paymentMode === "CREDIT" ? 800 : 600 }}>{paymentMode}</div>
          </>
        )}
      </div>

      <div style={{ textAlign: "center", marginTop: 6, paddingTop: 2, fontSize: 9, lineHeight: 1.35 }}>
        <div style={{ fontWeight: 700 }}>{shop?.bill_footer || "*** Thank You – Get Well Soon ***"}</div>
        <div style={{ fontSize: 8.5, marginTop: 2 }}>{shop?.bill_terms || "Goods once sold cannot be returned."}</div>
      </div>
    </div>
  );
}

function Row({ l, v }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
      <span>{l}</span>
      <span style={{ textAlign: "right", whiteSpace: "nowrap" }}>{v}</span>
    </div>
  );
}
